import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { ChevronRight } from "lucide-react";
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import InputDebounce from "../../../components/InputDebounce";
import { renderFieldIcon } from "../../../fields/FieldList";
import { FilterRuleProps } from "./AdvancedFilter";
import { colorCodes } from "../../../board/components/TaskDetail/ColorPicker";

export const supportedFieldTypes = [
  "string",
  "bool",
  "boolean",
  "double",
  "percent",
  "guid",
  "datetime",
  "stringArray",
  "account",
  "contact",
];

type FilterDropdownProps = {
  fields: {
    label: string;
    value: string;
    data: any;
  }[];
  isFilter: boolean;
  onClick: (data: FilterRuleProps) => void;
  onClose: () => void;
};

const FilterDropdown = ({ fields, isFilter, onClick, onClose }: FilterDropdownProps) => {
  const { t } = useTranslation();

  const [keywords, setKeywords] = useState("");

  const ruleReducer = useSelector((state: any) => state.ruleReducer);

  const renderFields = () => {
    return fields
      .filter((item) =>
        keywords ? item.label.trim().toLowerCase().includes(keywords.trim().toLowerCase()) : true
      )
      .map((item) => {
        const valueByFields = ruleReducer.get(item.value)?.toJS() || [];
        if (isFilter && item.data?.type !== "datetime" && valueByFields.length > 0) {
          return (
            <Popover key={item.value}>
              <PopoverButton className="w-full flex items-center justify-between space-x-2 p-2 cursor-default hover:bg-gray-100 outline-none">
                <div className="flex items-center space-x-2">
                  <div>{renderFieldIcon(item.data || {})}</div>
                  <div className="text-nowrap text-sm font-medium">{item.label}</div>
                </div>
                <div className="flex items-center justify-end">
                  <div className="flex items-center space-x-2">
                    <div className="w-max px-1 rounded-full text-center text-xs text-white bg-gray-600">
                      {valueByFields.length}
                    </div>
                    <ChevronRight className="w-4 h-4" />
                  </div>
                </div>
              </PopoverButton>
              <PopoverPanel anchor="right start" className="[--anchor-gap:28px] z-20">
                <div className="w-max max-h-120 overflow-y-auto p-2 rounded-md bg-white border border-gray-300">
                  {valueByFields.map((itemValue) => {
                    if (itemValue.color) {
                      const colorCode = colorCodes.find((cc) => cc.color === itemValue.color)?.code;
                      return (
                        <button
                          key={itemValue.value}
                          className="block w-full px-3 py-2 text-left text-xs font-medium hover:bg-gray-100"
                          onClick={() => {
                            onClick({
                              operator: "in",
                              fieldType: item.data?.type,
                              fieldId: item.value,
                              fieldValue: [itemValue.value],
                            });
                            onClose();
                          }}
                        >
                          <div
                            className={`w-max px-2 py-1 rounded-full ${
                              colorCode ? "" : "border border-gray-300"
                            }`}
                            style={{
                              backgroundColor: colorCode || "#fff",
                              color: colorCode ? "#fff" : "rgb(0, 0, 0)",
                            }}
                          >
                            {itemValue.label}
                          </div>
                        </button>
                      );
                    }
                    return (
                      <button
                        key={itemValue.value}
                        className="block w-full px-3 py-2 text-left text-sm font-medium hover:bg-gray-100"
                        onClick={() => {
                          onClick({
                            operator: "in",
                            fieldType: item.data?.type,
                            fieldId: item.value,
                            fieldValue: [itemValue.value],
                          });
                          onClose();
                        }}
                      >
                        {itemValue.label}
                      </button>
                    );
                  })}
                </div>
              </PopoverPanel>
            </Popover>
          );
        }
        return (
          <div
            key={item.value}
            onClick={() => {
              onClick({
                operator: "",
                fieldType: item.data?.type,
                fieldId: item.value,
                fieldValue: [],
              });
              onClose();
            }}
            className="flex items-center justify-between space-x-2 p-2 cursor-default hover:bg-gray-100"
          >
            <div className="flex items-center space-x-2">
              <div>{renderFieldIcon(item.data || {})}</div>
              <div className="text-nowrap text-sm font-medium">{item.label}</div>
            </div>
            <div className="flex items-center justify-end">
              <div className="w-10"></div>
            </div>
          </div>
        );
      });
  };

  return (
    <div className="w-full">
      <InputDebounce
        className={`w-full h-8 mb-2 px-2 placeholder:text-gray-400 text-sm border-b border-gray-200 focus:outline-none`}
        placeholder={t("Search fields...")}
        value={keywords}
        onChange={(value: string) => {
          setKeywords(value);
        }}
      />
      <div className="w-full max-h-120 overflow-y-auto">{renderFields()}</div>
    </div>
  );
};

export default FilterDropdown;
